import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Rail } from "./Rail";
import { Topbar } from "./Topbar";
import { MobileShell } from "./MobileShell";
import { CommandPalette } from "./CommandPalette";
import { useIsMobile } from "../lib/useIsMobile";
import { getPageFromHash, pagesById, setPageHash, type PageId } from "../pages/registry";
import { BridgeApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";

export function Shell() {
  const [page, setPage] = useState<PageId>(getPageFromHash);
  const [cmdOpen, setCmdOpen] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  // Hash routing
  useEffect(() => {
    const onHash = () => setPage(getPageFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const goto = (id: PageId) => {
    setPage(id);
    setPageHash(id);
  };

  // Cmd-K to open palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // r → restart agent-bridge (with confirm)
  const restart = useMutation({
    mutationFn: BridgeApi.restart,
    onSuccess: () => {
      toast.success("agent-bridge restart issued");
      qc.invalidateQueries({ queryKey: qk.bridge.status });
    },
    onError: (err) => toast.error("Restart failed", String(err)),
  });
  useHotkeys(
    "r",
    () => {
      if (confirm("Restart agent-bridge?")) restart.mutate();
    },
    { preventDefault: true, enableOnFormTags: false },
  );

  // g then m → mission control (vim-style)
  useHotkeys("g>m", () => goto("mission"));
  useHotkeys("g>r", () => goto("requests"));
  useHotkeys("g>l", () => goto("logs"));
  useHotkeys("g>b", () => goto("bus-inbox"));
  useHotkeys("g>s", () => goto("spawns"));

  const Page = pagesById[page].Component;
  const isMobile = useIsMobile();

  // Mobile gets its own chrome (bottom tab bar + compact header); the page
  // components are shared. The command palette is desktop-only — the mobile
  // bottom bar already exposes every destination.
  if (isMobile) {
    return <MobileShell page={page} onSelect={goto} />;
  }

  return (
    <div className="h-screen w-screen flex overflow-hidden text-sm">
      <Rail active={page} onSelect={goto} />
      <main className="flex-1 flex flex-col min-w-0 min-h-0">
        <Topbar page={page} onCmdK={() => setCmdOpen(true)} />
        <div className="flex-1 overflow-auto">
          <Page />
        </div>
      </main>
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} onGoto={goto} />
    </div>
  );
}
